import { Link } from "react-router-dom";
import type { FeedbackType, FeedbackStatus } from "@snaptics/api/src/types";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { X, FilterX } from "lucide-react";
import { cn } from "~/lib/utils";

interface FilterBarProps {
  filters: {
    type: FeedbackType | null;
    status: FeedbackStatus | null;
  };
  onFilterChange: (updates: {
    type?: FeedbackType | null;
    status?: FeedbackStatus | null;
    clearAll?: boolean;
  }) => string;
}

export function FilterBar({ filters, onFilterChange }: FilterBarProps) {
  const hasActiveFilters = filters.status || filters.type;

  const statuses: { value: FeedbackStatus; label: string }[] = [
    { value: "open", label: "Open" },
    { value: "under_review", label: "Under Review" },
    { value: "planned", label: "Planned" },
    { value: "in_progress", label: "In Progress" },
    { value: "done", label: "Done" },
    { value: "wont_do", label: "Won't Do" },
  ];

  // Format status label for display
  const getStatusLabel = (status: FeedbackStatus) => {
    const statusItem = statuses.find((s) => s.value === status);
    return statusItem?.label || status.replace("_", " ");
  };

  return (
    <div className="space-y-4">
      {/* Filter Controls - Card-like container matching table style */}
      <div className="rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          {/* Left side: Filter controls */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            {/* Type Filter - Link-based buttons */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-foreground">
                Type
              </label>
              <div className="inline-flex items-center rounded-md border bg-muted p-1 text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className={cn(
                    "min-w-[60px] rounded-sm px-3",
                    !filters.type && "bg-background text-foreground shadow-sm"
                  )}
                >
                  <Link to={onFilterChange({ type: null })} aria-label="Show all types">
                    All
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className={cn(
                    "min-w-[60px] rounded-sm px-3",
                    filters.type === "idea" && "bg-background text-foreground shadow-sm"
                  )}
                >
                  <Link to={onFilterChange({ type: "idea" })} aria-label="Show ideas only">
                    Ideas
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className={cn(
                    "min-w-[60px] rounded-sm px-3",
                    filters.type === "bug" && "bg-background text-foreground shadow-sm"
                  )}
                >
                  <Link to={onFilterChange({ type: "bug" })} aria-label="Show bugs only">
                    Bugs
                  </Link>
                </Button>
              </div>
            </div>

            {/* Visual separator between filters */}
            <Separator
              orientation="vertical"
              className="hidden h-10 self-end md:block"
            />

            {/* Status Filter - Compact button dropdown */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-foreground">
                Status
              </label>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  variant={!filters.status ? "default" : "outline"}
                  size="sm"
                  asChild
                >
                  <Link to={onFilterChange({ status: null })}>All</Link>
                </Button>
                {statuses.map((status) => (
                  <Button
                    key={status.value}
                    variant={filters.status === status.value ? "default" : "outline"}
                    size="sm"
                    asChild
                  >
                    <Link to={onFilterChange({ status: status.value })}>
                      {status.label}
                    </Link>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Right side: Clear all button */}
          {hasActiveFilters && (
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-muted-foreground hover:text-foreground"
                asChild
              >
                <Link to={onFilterChange({ clearAll: true })}>
                  <FilterX className="h-4 w-4" />
                  Clear All
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters Display - Shows applied filters as badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            Active filters:
          </span>
          {filters.type && (
            <Badge
              variant="secondary"
              className="gap-1.5 pl-2.5 pr-1.5 font-medium"
            >
              Type: {filters.type === "idea" ? "Ideas" : "Bugs"}
              <Link
                to={onFilterChange({ type: null })}
                className="rounded-sm hover:bg-secondary-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <X className="h-3 w-3" aria-label="Remove type filter" />
              </Link>
            </Badge>
          )}
          {filters.status && (
            <Badge
              variant="secondary"
              className="gap-1.5 pl-2.5 pr-1.5 font-medium"
            >
              Status: {getStatusLabel(filters.status)}
              <Link
                to={onFilterChange({ status: null })}
                className="rounded-sm hover:bg-secondary-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <X className="h-3 w-3" aria-label="Remove status filter" />
              </Link>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
